import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { AuthService } from '../../../services/common/auth.service';
import { DocumentService } from '../../../services/documents/document.service';
import { DocumentStoreService } from '../../../services/documents/document-store.service';
import { MatterStoreService } from '../../../services/matters/matter-store.service';
import { NotyService } from '../../../services/common/noty.service';
import { MatterService } from '../../../services/matters/matter.service';
@Component({
  selector: 'app-matter-documents-list',
  templateUrl: './matter-documents-list.component.html',
  styleUrls: ['./matter-documents-list.component.css'],
})
export class MatterDocumentsListComponent implements OnInit {
  /**
   * Matter id for documents list
   */
  @Input() matter_id: number;

  /**
   * Emits Event when user clicks for document show
   * @param {EventEmitter<any>    = new EventEmitter(} ) openDocument Event Emitter
   */
  @Output() openDocument: EventEmitter<any> = new EventEmitter();

  /**
   * Emites Event when url is for documents show
   * @param {EventEmitter<string> = new EventEmitter(} ) showDocuments Event Emitter
   */
  @Output() showDocuments: EventEmitter<string> = new EventEmitter();

  processBulkDeletionText = null;

  /**
   * Total document count
   * @type {number}
   */
  count: number = 0;

  /**
   * List of documents in current page
   * @type {Array<any>}
   */
  documents: Array<any> = [];

  /**
   * Indicates if page is loading
   * @type {boolean}
   */
  loading: boolean = true;

  documentsLoaded: boolean = false;

  processingBulkDeletion: boolean = false;

  selectedAll: boolean = false;

  selectedDocumentsCount: number = 0;

  /**
   * Documents search select options
   * @type {any}
   */
  search_options: any = {
    document_types: [],
    document_type_groups: [],
    document_type_tree: [],
    document_authors: [],
    tags: [],
  };

  /**
   * Document search params
   * @type {any}
   */
  search_params: any = {
    page: 1,
    per_page: 30,
    sort_type: 'Authored',
    document_type_ids: [],
    exclude_filters: false,
    show_email_thread_sources: false,
  };

  documentTypeFilterText: string = '';

  documentTypeVisibleTree: Array<any> = [];

  documentTypeGroupsExpanded: any = {};

  documentsRequestSequence: number = 0;

  sortSettings: any = {
    'Type': 'asc',
    'Created': 'desc',
    'Authored': 'desc',
    'Authors': 'asc',
    'Name': 'asc'
  };

  /**
   * Constructor
   * @param {AuthService}          private authService          Service for user auth and groups
   * @param {ActivatedRoute}       private activatedRoute       Angular Route for current route
   * @param {DocumentService}      private documentService      Service for accessing documents
   * @param {DocumentStoreService} private documentStoreService Service to manage document related components in current page
   * @param {MatterStoreService}   public                       matterStoreService Service to manage matter related components in current page
   */
  constructor(
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private documentService: DocumentService,
    private documentStoreService: DocumentStoreService,
    public matterStoreService: MatterStoreService,
    private notyService: NotyService,
    private matterService: MatterService
  ) {
    this.documentStoreService.matterDocumentsListComponent = this;
  }

  // Component Lifecycle functions

  /**
   * Called when component is initialized
   */
  ngOnInit() {
    if (this.activatedRoute.snapshot.queryParams['document_id']) {
      let document_id: number = Number(this.activatedRoute.snapshot.queryParams['document_id']);
      if (String(this.activatedRoute.snapshot.queryParams['show']) == 'true') {
        this.loadDocument({ id: document_id, name: this.activatedRoute.snapshot.queryParams['document_name'] });
        this.showDocuments.emit('show');
      } else if (String(this.activatedRoute.snapshot.queryParams['edit']) == 'true') {
        this.documentStoreService.documentFormComponent!.matter_id = Number(this.activatedRoute.snapshot.params['id']);
        this.editDocument(document_id, false);
        this.showDocuments.emit('show');
      }
    }
  }

  /**
   * Called when component variables change
   * @param {SimpleChanges} changes Angular changes object containing variables which have changed
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.matter_id && changes.matter_id.currentValue)
      this.documentService.getMatterSearchOptions(this.matter_id).then((response_json) => {
        this.search_options = response_json;
        this.search_params.per_page = response_json.per_page;
        this.search_params.matter_id = this.matter_id;
        this.search_options.document_authors.unshift({ label: 'All', value: '' });
        this.search_options.document_types.unshift({ label: 'All', value: '', name: 'All', prefix: '' });
        this.search_options.document_type_groups = this.search_options.document_type_groups || [];
        this.search_options.document_type_tree = this.search_options.document_type_tree || [];
        for (let node of this.search_options.document_type_tree) {
          this.documentTypeGroupsExpanded[node.key] = false;
        }
        this.refreshDocumentTypeVisibleTree();
        let maximumPrefixCount: number = 0;
        for (let documentType of this.search_options.document_types) {
          let length: number = (documentType.prefix || '').length;
          if (maximumPrefixCount < length) maximumPrefixCount = length;
        }
        for (let documentType of this.search_options.document_types) {
          let length: number = (documentType.prefix || '').length;
          if (maximumPrefixCount - length >= 0) documentType.padding = Array(maximumPrefixCount - length + 1).join('&nbsp;') + '&nbsp;';
          else documentType.padding = '&nbsp;';
        }
        this.loadDocuments();
      });
  }

  // Component functions

  /**
   * Limits document name to 15 characters
   * @param  {string} name Name of document to shorten
   * @return {string}      Returns shortened name
   */
  formatNameShort(name: string): string {
    return name.slice(0, 14) + '...' + name.split('.')[1];
  }

  /**
   * Edit document
   * @param {number}     document_id Id of document to edit
   * @param {boolean =           false}       promptBillingSave Indicates if billing save is to be prompted
   */
  editDocument(document_id: number, promptBillingSave: boolean = false) {
    this.documentService
      .getDocumentEdit(document_id, this.matter_id)
      .then(() => {
        if (promptBillingSave && this.matterStoreService.billingRecordTimeComponent)
          this.matterStoreService.billingRecordTimeComponent.promptBillingSave('document')!.then((value) => {
            if (!value || value == 'cancel') return;
            console.log('billing save');
            this.matterStoreService.matterShowComponent!.showDocumentFormTab();
            this.documentStoreService.documentFormComponent!.editDocument(document_id);
          });
        else if (this.authService.getUser() && this.authService.getUser().is('priviledged')) {
          console.log('priviledged');
          this.matterStoreService.matterShowComponent!.showDocumentFormTab();
          this.documentStoreService.documentFormComponent!.editDocument(document_id);
        }
      })
      .catch((getDocumentEditErr) => {
        if (getDocumentEditErr.status === 404) {
          this.matterService
            .reindexSolr(this.matter_id, ['documents'])
            .then((reIndexSolr) => {
              this.notyService.notify('Document does not exist, Reindexing Matter Documents', 'warning');
              this.loadDocuments();
            })
            .catch((reIndexSolrErr) => {
              console.log('reIndexSolrErr', reIndexSolrErr);
            });
        }
      });
  }

  /**
   * Download document - uses anchor element for reliable download
   * @param {Event} event Click event object
   * @param {number} documentId Document ID to download
   */
  downloadDocument(event: Event, documentId: number): boolean {
    // Prevent Angular router from intercepting
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Get authentication token from localStorage
    const token = window.localStorage.getItem('token');
    if (!token) {
      alert('Authentication token not found. Please log in again.');
      return false;
    }

    // Build download URL with token parameter for authentication
    const url = `/documents/${documentId}/download?disposition=attachment&token=${encodeURIComponent(token)}`;
    this.downloadInBackground(url);

    return false;
  }

  downloadInBackground(url: string): void {
    const iframeId = 'matter-document-download-frame';
    let iframe = document.getElementById(iframeId) as HTMLIFrameElement | null;

    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = iframeId;
      iframe.name = iframeId;
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }

    iframe.src = url;
  }

  /**
   * Load document show
   * @param {any} document Document for which document show is to open
   */
  loadDocument(document: any) {
    this.documentService
      .getDocumentShow(document.id)
      .then(() => {
        this.openDocument.emit(document);
      })
      .catch((getDocumentShowErr) => {
        if (getDocumentShowErr.status === 404) {
          this.matterService
            .reindexSolr(this.matter_id, ['documents'])
            .then(response_json => {
              this.notyService.notify('Document does not exist, Reindexing Matter Documents', 'warning');
              this.loadDocuments();
            });
        }
      });
  }

  /**
   * Load documents as per search params and page
   * @param {any    = null} pageNumber Current page number
   * @param {string = null} sortType   Sort type
   */
  loadDocuments(pageNumber: any = null, sortType: string | null = null) {
    if (pageNumber) this.search_params.page = pageNumber.page;
    if (sortType) this.setSortSetting(sortType);
    if (this.matter_id) this.search_params.matter_id = this.matter_id;
    const requestSequence = ++this.documentsRequestSequence;
    const searchParams = this.documentSearchParams();
    this.loading = true;
    this.documentService.getDocuments(searchParams).then((response_json) => {
      if (requestSequence !== this.documentsRequestSequence) return;
      if (response_json.documents) {
        this.documents = response_json.documents;
        this.count = response_json.count;
        this.loading = false;
        this.documentsLoaded = true;
        for (let doc of this.documents) if (doc.name && doc.name.length > 20) doc.short_name = this.formatNameShort(doc.name);
      } else {
        this.documents = [];
        this.count = 0;
        this.loading = false;
        this.documentsLoaded = true;
        this.notyService.notify('Error loading documents', 'error');
      }
      this.selectedAll = false;
      this.updateSelectedCount();
    }).catch((error) => {
      if (requestSequence !== this.documentsRequestSequence) return;
      this.loading = false;
      this.documentsLoaded = true;
      console.error('Error loading documents in matter show', error);
      this.notyService.notify('Error loading documents', 'error');
    });
  }

  documentSearchParams(): any {
    const searchParams = { ...this.search_params };
    if (searchParams.document_type_ids instanceof Array) {
      searchParams.document_type_ids = searchParams.document_type_ids.join(',');
    }
    return searchParams;
  }

  searchDocuments(excludeFilters: boolean) {
    this.search_params.exclude_filters = excludeFilters;
    this.search_params.page = 1;
    this.loadDocuments();
  }

  clearFilters() {
    this.search_params.document_type_id = '';
    this.search_params.document_type_ids = [];
    this.search_params.document_author_id = '';
    this.search_params.tags = [];
    this.search_params.content = '';
    this.search_params.page = 1;
    this.search_params.exclude_filters = false;
    this.search_params.show_email_thread_sources = false;
    this.documentTypeFilterText = '';
    this.refreshDocumentTypeVisibleTree();
    this.loadDocuments();
  }

  applyDocumentTypeFilter(filterText: string) {
    this.documentTypeFilterText = filterText || '';
    this.refreshDocumentTypeVisibleTree();
  }

  clearDocumentTypeFilter() {
    this.documentTypeFilterText = '';
    this.refreshDocumentTypeVisibleTree();
  }

  clearDocumentTypeSelection() {
    this.search_params.document_type_id = '';
    this.search_params.document_type_ids = [];
    this.search_params.page = 1;
    this.loadDocuments();
  }

  refreshDocumentTypeVisibleTree() {
    this.documentTypeVisibleTree = this.documentTypeNodesForDisplay(this.search_options.document_type_tree);
  }

  documentTypeArray(nodes: any): Array<any> {
    if (!nodes) return [];
    return Array.isArray(nodes) ? nodes : Object.values(nodes);
  }

  documentTypeNodesForDisplay(nodes: any = null): Array<any> {
    const filterText = (this.documentTypeFilterText || '').toLowerCase().trim();
    const sourceNodes = this.documentTypeArray(nodes === null ? this.search_options.document_type_tree : nodes);
    if (!filterText) return sourceNodes;

    let visibleNodes: Array<any> = [];
    for (let node of sourceNodes) {
      const nodeChildren = this.documentTypeArray(node.children);
      const visibleChildren = this.documentTypeNodesForDisplay(nodeChildren);
      const nodeMatches =
        (node.name || '').toLowerCase().includes(filterText) ||
        (node.prefix || '').toLowerCase().includes(filterText) ||
        (node.full_name || '').toLowerCase().includes(filterText);

      if (nodeMatches || visibleChildren.length > 0) {
        visibleNodes.push({ ...node, children: nodeMatches ? nodeChildren : visibleChildren });
      }
    }
    return visibleNodes;
  }

  documentTypeSelected(documentTypeId: any): boolean {
    return (this.search_params.document_type_ids || []).map(String).includes(String(documentTypeId));
  }

  documentTypeNodeIds(node: any): Array<string> {
    let ids: Array<string> = [];
    if (node.value) ids.push(String(node.value));
    for (let child of this.documentTypeArray(node.children)) {
      ids = ids.concat(this.documentTypeNodeIds(child));
    }
    return ids;
  }

  documentTypeNodeSelected(node: any): boolean {
    const ids = this.documentTypeNodeIds(node);
    return ids.length > 0 && ids.every((id: string) => this.documentTypeSelected(id));
  }

  documentTypeNodePartiallySelected(node: any): boolean {
    const ids = this.documentTypeNodeIds(node);
    return ids.some((id: string) => this.documentTypeSelected(id)) && !this.documentTypeNodeSelected(node);
  }

  documentTypeNodeExpanded(node: any): boolean {
    return this.documentTypeGroupsExpanded[node.key] || !!(this.documentTypeFilterText || '').trim();
  }

  documentTypeDisplayPrefix(node: any): string {
    const prefix = String((node && node.prefix) || '').trim();
    return prefix.length > 1 ? prefix : '';
  }

  selectedDocumentTypeCount(): number {
    return (this.search_params.document_type_ids || []).length;
  }

  toggleDocumentType(documentTypeId: any) {
    let documentTypeIds = (this.search_params.document_type_ids || []).map(String);
    const stringId = String(documentTypeId);
    if (documentTypeIds.includes(stringId)) {
      documentTypeIds = documentTypeIds.filter((id: string) => id !== stringId);
    } else {
      documentTypeIds.push(stringId);
    }
    this.search_params.document_type_ids = documentTypeIds;
    this.search_params.document_type_id = '';
    this.search_params.page = 1;
  }

  setDocumentTypeNodeFromEvent(node: any, event: Event) {
    const input = event.target as HTMLInputElement;
    this.setDocumentTypeNode(node, input.checked);
  }

  setDocumentTypeNode(node: any, selected: boolean) {
    let documentTypeIds = (this.search_params.document_type_ids || []).map(String);
    const nodeIds = this.documentTypeNodeIds(node);
    if (nodeIds.length < 1) return;

    if (selected) {
      for (let id of nodeIds) {
        if (!documentTypeIds.includes(id)) documentTypeIds.push(id);
      }
    } else {
      documentTypeIds = documentTypeIds.filter((id: string) => !nodeIds.includes(id));
    }

    this.search_params = {
      ...this.search_params,
      document_type_ids: documentTypeIds,
      document_type_id: '',
      page: 1
    };
    this.loadDocuments();
  }

  ocrDocument(document: any) {
    document.ocr_processing = true;
    this.documentService.ocrDocument(document.id)
      .then((response: any) => {
        document.ocr_processing = false;
        if (response.success) {
          document.ocr_done = true;
          this.notyService.notify('OCR completed successfully', 'success');
        } else {
          this.notyService.notify(response.message || 'OCR failed', 'error');
        }
      })
      .catch(() => {
        document.ocr_processing = false;
        this.notyService.notify('OCR failed', 'error');
      });
  }

  convertToWord(document: any): Promise<any> {
    if (!document || !document.id || document.word_conversion_processing) return Promise.resolve(false);

    document.word_conversion_processing = true;
    return this.documentService.convertToWord(document)
      .then((response: any) => {
        document.word_conversion_processing = false;
        if (response.success) {
          this.notyService.notify(response.text || 'Word document created', 'success');
          this.loadDocuments();
        } else {
          this.notyService.notify(response.text || 'PDF to Word conversion failed', 'error');
        }
        return response;
      })
      .catch((error: any) => {
        document.word_conversion_processing = false;
        const message = (error && error.error && error.error.text) || 'PDF to Word conversion failed';
        if (error && error.status === 409) {
          this.notyService.notify(message, 'warning');
          this.loadDocuments();
        } else {
          this.notyService.notify(message, 'error');
        }
        return error;
      });
  }

  processBulkDeletion() {
    this.processingBulkDeletion = true;
    let selectedDocumentIds: Array<number> = [];
    for (let documentObject of this.documents) {
      if (documentObject.selected) selectedDocumentIds.push(documentObject.id);
    }
    this.documentService
      .bulkOperation({ operation: 'delete', document_ids: selectedDocumentIds })
      .then((response_json) => {
        this.processingBulkDeletion = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Operation successful', 'success');
          this.loadDocuments();
        } else {
          this.notyService.notify(response_json.text || 'Error, please refresh and try again', 'error');
        }
      })
      .catch((err) => {
        this.processingBulkDeletion = false;
        this.notyService.notify('Error, please refresh and try again', 'error');
      });
  }

  selectAll() {
    for (let documentObject of this.documents) {
      documentObject.selected = this.selectedAll;
    }
    this.updateSelectedCount();
  }

  setSortSetting(sortType: string) {
    switch(sortType) {
      case 'Type':
      case 'Authors':
      case 'Authored':
      case 'Created':
      case 'Name':
      case 'Status':
        break;
      default:
        sortType = 'Authored';
    }
    if(this.search_params.sort_type == sortType){
      if(this.sortSettings[sortType] == 'asc'){
        this.sortSettings[sortType] = 'desc';
      }else{
        this.sortSettings[sortType] = 'asc';
      }
    }else{
      this.search_params.sort_type        = sortType;
    }
    this.search_params.hidden_sort_type = sortType;
    this.search_params.sort_direction   = this.sortSettings[sortType];
  }

  updateSelectedCount() {
    this.selectedDocumentsCount = 0;
    for (let documentObject of this.documents) {
      if (documentObject.selected) this.selectedDocumentsCount += 1;
    }
  }
}
